import React, { useCallback, useEffect, useState } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { NO_CAPTCHA_REQUIRED_STATUS } from 'src/dataLayer/localData/captcha.js'
import { useAppConfigContext } from 'src/viewLayer/components/contexts/AppConfigContext'
import { useCaptchaConfigContext } from 'src/viewLayer/components/contexts/CaptchaConfigContext'
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext'
import DefaultLoginRedirect from 'src/viewLayer/components/redirects/DefaultLoginRedirect'

import { usePasswordPagePath } from '../../../navigation'
import useInputState from '../../hooks/useInputState'
// Components
import LoginEmailPage from '../../pages/LoginEmailPage'
import LoginPasswordPage from '../../pages/LoginPasswordPage'
import { CaptchaFeature } from '../../shared/CaptchaFeature/CaptchaFeature'
// Form submission
import onEmailSubmit from './onEmailSubmit'
import onPasswordSubmit from './onPasswordSubmit'
import useHandleAuthTokenRequest from './useHandleAuthTokenRequest'
// Hooks
import useHandleSessionRequest from './useHandleSessionRequest'

const EMAIL_ROUTE_PATH = 'email'
const PASSWORD_ROUTE_PATH = 'password'

/*
 * Parent component of the login sub-pages, carries out the login operations.
 */
export default function EmailLoginFlow(properties) {
  const navigate = useNavigate()
  const passwordPagePath = usePasswordPagePath()
  const { redirectTo } = useRouteParamsContext()
  const { devicePath } = useAppConfigContext()
  const { setCaptchaConfig } = useCaptchaConfigContext()
  const [isPasswordButtonClicked, setIsPasswordButtonClicked] = useState(false)

  const initEmail = {
    errorKey: '',
    // eslint-disable-next-line no-use-before-define -- Legacy
    inputValue: properties.localStorageEmail || email || '',
    isFocused: true,
  }

  // State
  const {
    errorKey: emailErrorKey,
    inputValue: email,
    isFocused: emailIsFocused,
    resetInputState: resetEmailState,
    setErrorKey: setEmailErrorKey,
    setInputValue: setEmail,
  } = useInputState(initEmail)

  const {
    errorKey: passwordErrorKey,
    inputValue: password,
    isFocused: passwordIsFocused,
    resetInputState: resetPasswordState,
    setErrorKey: setPasswordErrorKey,
    setInputValue: setPassword,
  } = useInputState()

  // Endpoints
  const { authSessionRequestInProgress, captchaConfig, makeAuthSessionRequest, sessionCode } =
    useHandleSessionRequest(email)

  useEffect(() => {
    if (captchaConfig) {
      setCaptchaConfig({ siteKey: captchaConfig.params.site_key })
    } else if (sessionCode) {
      setCaptchaConfig({ siteKey: NO_CAPTCHA_REQUIRED_STATUS })
    }
  }, [sessionCode, captchaConfig, setCaptchaConfig])

  const getInitialCaptchaResponseTokenValue = useCallback(
    () => (captchaConfig?.params?.site_key ? undefined : NO_CAPTCHA_REQUIRED_STATUS),
    [captchaConfig],
  )

  const [captchaResponseToken, setCaptchaResponseToken] = useState(
    getInitialCaptchaResponseTokenValue(),
  )

  useEffect(() => {
    setCaptchaResponseToken(getInitialCaptchaResponseTokenValue())
  }, [sessionCode, getInitialCaptchaResponseTokenValue])

  const { authTokenRequestInProgress, makeAuthTokenRequest } = useHandleAuthTokenRequest({
    captchaResponseToken,
    email,
    password,
    sessionCode,
  })

  // Form submission props
  const emailSubmitParameters = {
    email,
    makeAuthSessionRequest,
    passwordPagePath,
    resetPasswordState,
    setEmailErrorKey,
  }

  const passwordSubmitParameters = {
    captchaResponseToken,
    devicePath,
    makeAuthSessionRequest,
    makeAuthTokenRequest,
    redirectTo,
    setPasswordErrorKey,
  }

  useEffect(() => {
    if (isPasswordButtonClicked && captchaResponseToken) {
      setIsPasswordButtonClicked(false)
      onPasswordSubmit(passwordSubmitParameters)
    }
  }, [captchaResponseToken, isPasswordButtonClicked])

  // JSX
  // invisible captcha needs to know if there was an error with the password, so that we can manually trigger it again
  const captchaFeature = (
    <CaptchaFeature
      captchaConfig={captchaConfig}
      setCaptchaToken={setCaptchaResponseToken}
      shouldExecuteCaptcha={isPasswordButtonClicked}
    />
  )

  const isPasswordLoginInProgress = authTokenRequestInProgress || isPasswordButtonClicked

  return (
    <Routes>
      <Route
        element={
          <LoginEmailPage
            appType={properties.appType}
            email={email}
            errorKey={emailErrorKey}
            inProgress={authSessionRequestInProgress}
            isFocused={emailIsFocused}
            onChange={setEmail}
            onReset={resetEmailState}
            onSubmit={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onEmailSubmit(emailSubmitParameters, navigate)
            }}
            path={EMAIL_ROUTE_PATH}
          />
        }
        path={EMAIL_ROUTE_PATH}
      />
      <Route
        element={
          <LoginPasswordPage
            appType={properties.appType}
            captchaFeature={captchaFeature}
            captchaResponseToken={captchaResponseToken}
            email={email}
            errorKey={passwordErrorKey}
            isFocused={passwordIsFocused}
            isPasswordLoginInProgress={isPasswordLoginInProgress}
            onChange={setPassword}
            onSubmit={() => {
              setIsPasswordButtonClicked(true)
            }}
            password={password}
            path={PASSWORD_ROUTE_PATH}
          />
        }
        path={PASSWORD_ROUTE_PATH}
      />
      <Route element={<DefaultLoginRedirect />} path="*" />
    </Routes>
  )
}
